/**
 * Appeals cases — sample records for All Appeals / Appeal Detail.
 * Original Decision IDs keep each owner's identity (no shared DEC scheme).
 */

export const APPEAL_STATUS_OPTIONS = [
  'Submitted',
  'Under Review',
  'Awaiting Reply',
  'Decided',
  'Withdrawn',
];

/** Normalize legacy Appeal statuses to current names. */
export function normalizeAppealStatus(status) {
  if (status === 'New') return 'Submitted';
  if (status === 'In Progress') return 'Under Review';
  return status || 'Submitted';
}

/** Formal decision owners eligible for Appeals (Screen 1 Source filter). */
export const APPEAL_SOURCE_OPTIONS = ['Moderation', 'Compliance', 'Fraud & Security'];

export const APPEAL_ACCOUNT_TYPE_OPTIONS = ['Customer', 'Business'];

/** Case Type column / Type filter (Moderation, Compliance, Fraud & Security). */
export const APPEAL_CASE_TYPE_OPTIONS = [
  'Post',
  'Review',
  'Message',
  'Licence',
  'Badge',
  'Account Issue',
  'Suspected account takeover',
];

export const APPEAL_OUTCOME_OPTIONS = ['Appeal Approved', 'Appeal Rejected'];

export const APPEAL_HANDLER_OPTIONS = ['Alex Morgan', 'Priya Shah', 'Hamza Ali', 'Sara Khan'];

/**
 * Interface examples from the Appeals records spec.
 * Bracketed original-decision references use each owner's actual ID formats.
 */
export function buildSampleAppealCases() {
  const baseRows = [
    {
      appealId: 'APL-000107',
      originalDecisionId: 'DEC-FS-002105',
      submittedByName: 'Daniel Brooks',
      accountId: 'CUS-002105',
      accountType: 'Customer',
      source: 'Fraud & Security',
      caseType: 'Account Issue',
      decisionType: 'Final Warning',
      assignedHandler: 'Sara Khan',
      status: 'Decided',
      submittedOn: new Date('2026-08-08T13:10:00'),
      lastUpdated: new Date('2026-08-08T16:25:00'),
    },
    {
      appealId: 'APL-000108',
      originalDecisionId: 'DEC-FS-001633',
      submittedByName: 'Westline Carpentry',
      accountId: 'BUS-001633',
      accountType: 'Business',
      source: 'Fraud & Security',
      caseType: 'Suspected account takeover',
      decisionType: 'Permanent Ban',
      assignedHandler: 'Priya Shah',
      status: 'Withdrawn',
      submittedOn: new Date('2026-08-08T14:35:00'),
      lastUpdated: new Date('2026-08-08T15:05:00'),
    },
    {
      appealId: 'APL-000105',
      originalDecisionId: 'MOD-DEC-2411',
      submittedByName: 'Olivia Chen',
      accountId: 'CUS-001941',
      accountType: 'Customer',
      source: 'Moderation',
      caseType: 'Review',
      decisionType: 'Review — Confirmed',
      assignedHandler: 'Priya Shah',
      status: 'Awaiting Reply',
      submittedOn: new Date('2026-08-08T11:05:00'),
      lastUpdated: new Date('2026-08-08T14:10:00'),
    },
    {
      appealId: 'APL-000104',
      originalDecisionId: 'MOD-DEC-0785',
      submittedByName: 'Harbour Plumbing',
      accountId: 'BUS-001284',
      accountType: 'Business',
      source: 'Moderation',
      caseType: 'Post',
      decisionType: 'Post — Confirmed',
      assignedHandler: 'Alex Morgan',
      status: 'Under Review',
      submittedOn: new Date('2026-08-08T10:15:00'),
      lastUpdated: new Date('2026-08-08T13:40:00'),
    },
    {
      appealId: 'APL-000106',
      originalDecisionId: 'CMP-DEC-1498',
      submittedByName: 'Northside Electrical',
      accountId: 'BUS-001498',
      accountType: 'Business',
      source: 'Compliance',
      caseType: 'Licence',
      decisionType: 'Licence Rejected',
      assignedHandler: null,
      status: 'Submitted',
      submittedOn: new Date('2026-08-08T12:20:00'),
      lastUpdated: new Date('2026-08-08T12:20:00'),
    },
    {
      appealId: 'APL-000103',
      originalDecisionId: 'MOD-DEC-0540',
      submittedByName: 'Mia Torres',
      accountId: 'CUS-001880',
      accountType: 'Customer',
      source: 'Moderation',
      caseType: 'Message',
      decisionType: 'Message — Confirmed',
      assignedHandler: null,
      status: 'Submitted',
      submittedOn: new Date('2026-08-07T16:40:00'),
      lastUpdated: new Date('2026-08-07T16:40:00'),
    },
    {
      appealId: 'APL-000102',
      originalDecisionId: 'CMP-DEC-1210',
      submittedByName: 'Rapid Electrical',
      accountId: 'BUS-001210',
      accountType: 'Business',
      source: 'Compliance',
      caseType: 'Badge',
      decisionType: 'Badge Rejected',
      assignedHandler: 'Hamza Ali',
      status: 'Under Review',
      submittedOn: new Date('2026-08-07T11:15:00'),
      lastUpdated: new Date('2026-08-08T09:50:00'),
    },
    {
      appealId: 'APL-000101',
      originalDecisionId: 'MOD-DEC-0478',
      submittedByName: 'Ethan Walsh',
      accountId: 'CUS-001760',
      accountType: 'Customer',
      source: 'Moderation',
      caseType: 'Review',
      decisionType: 'Review — Confirmed',
      assignedHandler: 'Sara Khan',
      status: 'Decided',
      submittedOn: new Date('2026-08-06T14:05:00'),
      lastUpdated: new Date('2026-08-07T10:20:00'),
    },
    {
      appealId: 'APL-000100',
      originalDecisionId: 'DEC-FS-001155',
      submittedByName: 'Summit Plumbing Co.',
      accountId: 'BUS-001155',
      accountType: 'Business',
      source: 'Fraud & Security',
      caseType: 'Licence',
      decisionType: 'Final Warning',
      assignedHandler: 'Priya Shah',
      status: 'Awaiting Reply',
      submittedOn: new Date('2026-08-05T09:30:00'),
      lastUpdated: new Date('2026-08-06T15:45:00'),
    },
    {
      appealId: 'APL-000099',
      originalDecisionId: 'MOD-DEC-0412',
      submittedByName: 'Aisha Khan',
      accountId: 'CUS-001640',
      accountType: 'Customer',
      source: 'Moderation',
      caseType: 'Review',
      decisionType: 'Review — Confirmed',
      assignedHandler: 'Alex Morgan',
      status: 'Withdrawn',
      submittedOn: new Date('2026-08-04T13:20:00'),
      lastUpdated: new Date('2026-08-04T17:10:00'),
    },
  ];

  return baseRows.map((appeal, idx) => ({
    ...appeal,
    /** @deprecated use `source` — kept for older navigation state */
    sourceModule: appeal.source,
    accountAvatar:
      appeal.accountType === 'Customer'
        ? `https://i.pravatar.cc/150?img=${(idx % 70) + 1}`
        : `https://i.pravatar.cc/150?img=${(idx % 70) + 20}`,
  }));
}

export function displayHandlerName(handler) {
  if (!handler || String(handler).trim() === '') return 'Unassigned';
  return handler;
}

/**
 * Case type for the Appeals list Type column.
 * Moderation: Post / Review / Message / Quote / …
 * Compliance: Licence / Badge / Trade Ticket
 * Fraud & Security: Post / Licence / Account Issue / Suspected account takeover
 */
export function getAppealCaseType(appeal) {
  if (appeal?.caseType) return appeal.caseType;

  const decisionType = String(appeal?.decisionType || '');
  const source = appeal?.source || appeal?.sourceModule || '';

  if (source === 'Moderation' || decisionType.includes('—')) {
    if (decisionType.includes('Business Reply')) return 'Business Reply';
    if (decisionType.includes('Business Profile')) return 'Business Profile Content';
    if (decisionType.includes('Message')) return 'Message';
    if (decisionType.includes('Quote')) return 'Quote';
    if (decisionType.includes('Review')) return 'Review';
    if (decisionType.includes('Post')) return 'Post';
  }

  if (source === 'Compliance' || /Licence|Badge|Ticket/i.test(decisionType)) {
    if (decisionType.includes('Badge')) return 'Badge';
    if (/Trade Ticket|Ticket/i.test(decisionType)) return 'Trade Ticket';
    if (decisionType.includes('Licence')) return 'Licence';
  }

  if (source === 'Fraud & Security') {
    return 'Account Issue';
  }

  return decisionType || '—';
}

/** Applied Rule snapshot captured at successful submission (read-only on the Appeal). */
export function getAppliedRuleSnapshot(appeal) {
  const source = appeal?.source || appeal?.sourceModule || 'Moderation';
  const decisionType = appeal?.decisionType || 'Post — Confirmed';
  const accountType = appeal?.accountType || 'Business';

  if (source === 'Moderation' && String(decisionType).includes('Post')) {
    return {
      ruleId: 'APC-0002',
      scope: 'Moderation',
      decisionType: 'Post — Confirmed',
      accountSelection: accountType,
      availabilityAtSubmission: 'Enabled',
      correctiveAction: 'Overturn Post Decision',
      conditions: 'No Conditions',
      appealWindow: '7 Calendar Days',
      actionOwner: 'Posts',
    };
  }
  if (source === 'Moderation' && String(decisionType).includes('Review')) {
    return {
      ruleId: 'APC-0003',
      scope: 'Moderation',
      decisionType: 'Review — Confirmed',
      accountSelection: accountType,
      availabilityAtSubmission: 'Enabled',
      correctiveAction: 'Overturn Review Decision',
      conditions: 'No Conditions',
      appealWindow: '7 Calendar Days',
      actionOwner: 'Reviews',
    };
  }
  if (source === 'Compliance') {
    const isBadge = String(decisionType).includes('Badge');
    const isTicket = String(decisionType).includes('Ticket');
    return {
      ruleId: isTicket ? 'APC-0006' : isBadge ? 'APC-0005' : 'APC-0004',
      scope: 'Compliance',
      decisionType: decisionType || 'Licence Rejected',
      accountSelection: 'Business',
      availabilityAtSubmission: isTicket ? 'Disabled' : 'Enabled',
      correctiveAction: isTicket
        ? 'Approve Ticket Application'
        : isBadge
          ? 'Approve Badge Application'
          : 'Approve Licence Application',
      conditions: 'No Conditions',
      appealWindow: '7 Calendar Days',
      actionOwner: 'Compliance',
    };
  }
  if (source === 'Fraud & Security') {
    const isBan = String(decisionType).includes('Ban');
    return {
      ruleId: isBan ? 'APC-0001' : 'APC-0007',
      scope: 'Fraud & Security',
      decisionType: decisionType || 'Final Warning',
      accountSelection: accountType,
      availabilityAtSubmission: 'Enabled',
      correctiveAction: isBan ? 'Lift Permanent Ban' : 'Cancel Final Warning',
      conditions: 'No Conditions',
      appealWindow: '7 Calendar Days',
      actionOwner: 'Fraud & Security',
    };
  }
  return {
    ruleId: 'APC-0002',
    scope: source,
    decisionType,
    accountSelection: accountType,
    availabilityAtSubmission: 'Enabled',
    correctiveAction: 'Overturn Decision',
    conditions: 'No Conditions',
    appealWindow: '7 Calendar Days',
    actionOwner: source,
  };
}

export function getCorrectiveActionLabel(appeal) {
  const applied = getAppliedRuleSnapshot(appeal);
  const recordId = appeal?.affectedRecordId;
  if (applied.correctiveAction === 'Overturn Post Decision' && recordId) {
    return `${applied.correctiveAction} — ${recordId}`;
  }
  if (applied.correctiveAction === 'Overturn Review Decision' && recordId) {
    return `${applied.correctiveAction} — ${recordId}`;
  }
  return applied.correctiveAction;
}

export function getActionOwnerLabel(appeal) {
  return getAppliedRuleSnapshot(appeal).actionOwner;
}

export function getActionOwnerPath(appeal) {
  const owner = getActionOwnerLabel(appeal);
  if (owner === 'Posts') return '/admin/posts/all';
  if (owner === 'Reviews') return '/admin/reviews';
  if (owner === 'Compliance') return '/admin/licences-badges-trade-tickets';
  if (owner === 'Fraud & Security') return '/admin/fraud/alerts';
  return '/admin/dashboard';
}

export function getSourceRecordPath(appeal) {
  const source = appeal?.source || appeal?.sourceModule;
  if (source === 'Moderation') {
    return `/admin/moderation/queue/${appeal?.relatedModerationCaseId || 'MOD-002411'}`;
  }
  if (source === 'Compliance') {
    return `/admin/licences-badges-trade-tickets/${appeal?.affectedRecordId || ''}`;
  }
  if (source === 'Fraud & Security') {
    return `/admin/fraud/alerts/${appeal?.relatedFraudCaseId || appeal?.relatedModerationCaseId || ''}`;
  }
  return '/admin/dashboard';
}

export function getOriginalDecisionPath(appeal) {
  return getSourceRecordPath(appeal);
}
